import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase-config';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [signupSuccess, setSignupSuccess] = useState(false);
  const loadingTimerRef = useRef(null);
  const location = useLocation();
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [fromPayment, setFromPayment] = useState(false);
  const [signupFirst, setSignupFirst] = useState(false);

  useEffect(() => {
    console.log('Signup component mounted, current path:', window.location.pathname);

    // First check location state (from App.jsx redirect)
    const fromPaymentState = location.state?.fromPayment;
    const stateSessionId = location.state?.sessionId;

    if (fromPaymentState && stateSessionId) {
      console.log('Found session ID in location state:', stateSessionId);
      // Store in localStorage in case the page refreshes
      localStorage.setItem('stripeSessionId', stateSessionId);
      setFromPayment(true);
    }

    // Check if we're using the new signup-first flow
    const signupFirstFlag = localStorage.getItem('signupFirst');
    if (signupFirstFlag === 'true') {
      console.log('Using signup-first flow');
      setSignupFirst(true);
    }

    // Then check URL parameters
    const params = new URLSearchParams(location.search);
    let subId = params.get('subscriptionId');
    let busId = params.get('businessId');
    let sessionId = params.get('session_id');

    // Check for plan parameter
    const plan = params.get('plan');
    if (plan) {
      console.log('Found plan in URL:', plan);
      localStorage.setItem('selectedPlan', plan);
      // Default to monthly billing
      localStorage.setItem('billingCycle', 'monthly');
    }

    // If not in URL, check localStorage (from bridge page)
    if (!subId) {
      subId = localStorage.getItem('subscriptionId');
      if (subId) {
        console.log('Found subscription ID in localStorage:', subId);
        // Clear from localStorage after retrieving
        localStorage.removeItem('subscriptionId');
      }
    } else {
      console.log('Found subscription ID in URL:', subId);
    }

    if (!busId) {
      busId = localStorage.getItem('businessId');
      if (busId) {
        console.log('Found business ID in localStorage:', busId);
        // Clear from localStorage after retrieving
        localStorage.removeItem('businessId');
      }
    } else {
      console.log('Found business ID in URL:', busId);
    }

    // Check for Stripe session ID from payment-success bridge page
    if (!sessionId) {
      sessionId = localStorage.getItem('stripeSessionId');
      if (sessionId) {
        console.log('Found Stripe session ID in localStorage:', sessionId);
        // Don't remove it yet - we'll need it for the signup process
      }
    } else {
      console.log('Found Stripe session ID in URL:', sessionId);
      // Store in localStorage in case the page refreshes
      localStorage.setItem('stripeSessionId', sessionId);
    }

    // Set state with the IDs we found
    if (subId) {
      setSubscriptionId(subId);
    }

    if (busId) {
      setBusinessId(busId);
    }

    return () => {
      console.log('Signup component unmounting');
      // Clear the loading timer if component unmounts
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [location]);

  // Monitor loading state for timeouts
  useEffect(() => {
    if (loading) {
      // Set a timer to check if loading gets stuck, but make it 20 seconds instead of 10
      loadingTimerRef.current = setTimeout(() => {
        console.log('Loading state timeout reached, but continuing to wait');
        // Don't navigate away - just log the timeout
      }, 20000); // 20 seconds timeout
    } else {
      // Clear the timer if loading state changes to false
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading, navigate]);

  // Password validation function
  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }

    // Check for at least one number
    if (!/\d/.test(password)) {
      return "Password must contain at least one number";
    }

    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }

    return null; // Password is valid
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError("Passwords do not match");
    }

    // Validate password strength
    const passwordError = validatePassword(password);
    if (passwordError) {
      return setError(passwordError);
    }

    try {
      setError("");
      setLoading(true);

      console.log('Attempting signup with:', { email });

      // Store email in localStorage for fallback identification
      localStorage.setItem('signupEmail', email);

      // Attempt to sign up the user
      const result = await signup(email, password);
      console.log('Signup successful:', result);

      // Determine the user object and user ID
      let user, userId;

      // Check if result is the user object itself (J)
      if (result && result.uid) {
        console.log('Result is the user object itself');
        user = result;
        userId = result.uid;
      }
      // Check if result has user property
      else if (result && result.user && result.user.uid) {
        console.log('Result has user property');
        user = result.user;
        userId = result.user.uid;
      }
      // Fallback to current user
      else {
        console.log('Using auth.currentUser as fallback');
        user = auth.currentUser;
        if (user && user.uid) {
          userId = user.uid;
        } else {
          console.error('No valid user available after signup');
          setError("Failed to create an account properly. Please try again.");
          return; // Exit early
        }
      }

      console.log('Using user ID:', userId);

      // Check for Stripe session ID from payment-success bridge page
      const stripeSessionId = localStorage.getItem('stripeSessionId');

      // If we have a Stripe session ID, create a business record
      if (stripeSessionId) {
        try {
          // Create a business record with the user ID and Stripe session ID
          const businessRef = doc(db, 'businesses', userId);
          await setDoc(businessRef, {
            userId: userId,
            stripeSessionId: stripeSessionId,
            paymentComplete: true,
            createdAt: new Date(),
            updatedAt: new Date(),
            // Set minimal required fields to avoid errors
            businessInfo: {
              name: '',
              description: ''
            },
            phoneData: {
              phoneNumber: '',
              carrier: ''
            },
            introMessage: '',
            commonQuestions: []
          });

          console.log('Created business record with Stripe session ID');

          // Update user profile
          const userRef = doc(db, 'users', userId);
          await setDoc(userRef, {
            email: email,
            businessId: userId,  // Use the user ID as the business ID
            paymentComplete: true,
            createdAt: new Date(),
            updatedAt: new Date()
          }, { merge: true });

          console.log('Updated user profile with business ID');

          // Clear the Stripe session ID from localStorage
          localStorage.removeItem('stripeSessionId');
        } catch (e) {
          console.error('Error creating business record with Stripe session ID:', e);
        }
      }

      // If we have subscription and business IDs from the payment flow, update the user profile
      else if (subscriptionId || businessId) {
        try {
          // userId is already defined above
          const userRef = doc(db, 'users', userId);

          // Update user profile with subscription and business info
          await updateDoc(userRef, {
            subscriptionId: subscriptionId || null,
            businessId: businessId || null,
            paymentComplete: true,
            updatedAt: new Date()
          });

          console.log('Updated user profile with subscription and business info');

          // If we have a business ID, update the business record with the user ID
          if (businessId) {
            const businessRef = doc(db, 'businesses', businessId);
            await updateDoc(businessRef, {
              userId: userId,
              updatedAt: new Date()
            });
            console.log('Updated business record with user ID');
          }
        } catch (e) {
          console.error('Error updating user profile with subscription info:', e);
        }
      }

      // Set the success state
      setSignupSuccess(true);

      // Import setOnboardingMode directly to ensure it's set
      try {
        const { setOnboardingMode } = await import('../utils/firestoreConnectionManager');
        setOnboardingMode(true);
        console.log('Onboarding mode enabled from Signup component');
      } catch (e) {
        console.error('Failed to set onboarding mode from Signup component:', e);
      }

      // Create a minimal business record for the user
      try {
        // userId is already defined above
        const businessRef = doc(db, 'businesses', userId);

        // Create a minimal business record
        await setDoc(businessRef, {
          userId: userId,
          createdAt: new Date(),
          updatedAt: new Date(),
          onboardingComplete: false // Mark as incomplete so we can complete it later if needed
        });

        console.log('Created minimal business record with ID:', userId);

        // Check if user came from payment
        const stripeSessionId = localStorage.getItem('stripeSessionId');

        if (stripeSessionId || fromPayment) {
          // If from payment, redirect to onboarding
          console.log('User came from payment, redirecting to onboarding');
          setTimeout(() => {
            console.log('Navigation timeout reached, forcing redirect to onboarding');
            window.location.href = '/onboarding';
          }, 2000);
        } else {
          // Check if user came from plan selection
          const selectedPlan = localStorage.getItem('selectedPlan');
          const billingCycle = localStorage.getItem('billingCycle');

          if (selectedPlan) {
            console.log(`User selected plan: ${selectedPlan}, billing cycle: ${billingCycle}`);

            // Store the user ID for the payment process and webhook
            localStorage.setItem('userId', userId);

            // Determine which payment link to use
            let paymentLink;

            if (window.PAYMENT_LINKS) {
              console.log('Using global PAYMENT_LINKS from stripe-config.js');
              console.log('Stripe mode:', window.STRIPE_MODE);

              if (selectedPlan === 'standard') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.STANDARD_ANNUAL : window.PAYMENT_LINKS.STANDARD_MONTHLY;
              } else if (selectedPlan === 'business') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.BUSINESS_ANNUAL : window.PAYMENT_LINKS.BUSINESS_MONTHLY;
              } else if (selectedPlan === 'pro') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.PRO_ANNUAL : window.PAYMENT_LINKS.PRO_MONTHLY;
              }

              console.log(`Using ${window.STRIPE_MODE} mode payment link:`, paymentLink);
            } else {
              console.error('Global PAYMENT_LINKS not found. Make sure stripe-config.js is loaded.');

              // No fallback to hardcoded links - this would cause issues with test/live mode
              console.error('Cannot proceed without payment links from stripe-config.js');
              setError('Configuration error. Please refresh the page and try again.');
              return;
            }

            if (paymentLink) {
              // Add user ID as a parameter to the payment link
              const separator = paymentLink.includes('?') ? '&' : '?';
              const finalUrl = `${paymentLink}${separator}client_reference_id=${userId}&userId=${userId}`;

              console.log('Redirecting to Stripe payment page:', finalUrl);

              // Clear the selected plan from localStorage
              localStorage.removeItem('selectedPlan');
              localStorage.removeItem('billingCycle');

              // Redirect to Stripe payment page
              setTimeout(() => {
                window.location.href = finalUrl;
              }, 2000);
            } else {
              // Fallback to onboarding if payment link not found
              console.error('Payment link not found for selected plan');
              setTimeout(() => {
                window.location.href = '/onboarding';
              }, 2000);
            }
          } else if (signupFirst) {
            // If using signup-first flow, redirect to onboarding
            console.log('Using signup-first flow, redirecting to onboarding');
            // Clear the signup-first flag
            localStorage.removeItem('signupFirst');
            // Store the user ID for the payment process
            localStorage.setItem('userId', userId);
            setTimeout(() => {
              console.log('Navigation timeout reached, forcing redirect to onboarding');
              window.location.href = '/onboarding';
            }, 2000);
          } else {
            // Always redirect to onboarding after signup if no plan selected
            console.log('No plan selected, redirecting to onboarding page');
            setTimeout(() => {
              console.log('Navigation timeout reached, forcing redirect to onboarding');
              window.location.href = '/onboarding';
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error creating business record:', error);

        // Make sure we have the user ID
        let userId;
        try {
          // Check if result exists and has the expected structure
          if (result && result.user && result.user.uid) {
            userId = result.user.uid;
            console.log('Using user ID from result:', userId);
          } else {
            // If we don't have a valid result, try to get the current user from auth
            const currentUser = auth.currentUser;
            if (currentUser) {
              userId = currentUser.uid;
              console.log('Using user ID from currentUser:', userId);
            } else {
              console.error('No user ID available, using fallback for plan selection');

              // Check if user selected a plan
              const selectedPlan = localStorage.getItem('selectedPlan');
              const billingCycle = localStorage.getItem('billingCycle') || 'monthly';

              if (selectedPlan) {
                // Get the email from localStorage
                const userEmail = localStorage.getItem('signupEmail');

                if (!userEmail) {
                  console.error('No email available for client reference, redirecting to signup');
                  setTimeout(() => {
                    window.location.href = `/signup?plan=${selectedPlan}`;
                  }, 2000);
                  return;
                }

                // Use email as client reference for webhook identification
                const emailReference = encodeURIComponent(userEmail);

                // Determine which payment link to use
                let paymentLink;

                if (window.PAYMENT_LINKS) {
                  console.log('Using global PAYMENT_LINKS from stripe-config.js');
                  console.log('Stripe mode:', window.STRIPE_MODE);

                  if (selectedPlan === 'standard') {
                    paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.STANDARD_ANNUAL : window.PAYMENT_LINKS.STANDARD_MONTHLY;
                  } else if (selectedPlan === 'business') {
                    paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.BUSINESS_ANNUAL : window.PAYMENT_LINKS.BUSINESS_MONTHLY;
                  } else if (selectedPlan === 'pro') {
                    paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.PRO_ANNUAL : window.PAYMENT_LINKS.PRO_MONTHLY;
                  }

                  console.log(`Using ${window.STRIPE_MODE} mode payment link:`, paymentLink);
                } else {
                  console.error('Global PAYMENT_LINKS not found. Make sure stripe-config.js is loaded.');

                  // No fallback to hardcoded links - this would cause issues with test/live mode
                  console.error('Cannot proceed without payment links from stripe-config.js');
                  setError('Configuration error. Please refresh the page and try again.');
                  return;
                }

                if (paymentLink) {
                  // Add email reference to the payment link
                  const separator = paymentLink.includes('?') ? '&' : '?';
                  const finalUrl = `${paymentLink}${separator}client_reference_id=${emailReference}&email=${emailReference}`;

                  console.log('Redirecting to Stripe payment page with email reference:', finalUrl);

                  // Clear the selected plan from localStorage
                  localStorage.removeItem('selectedPlan');
                  localStorage.removeItem('billingCycle');

                  // Redirect to Stripe payment page
                  setTimeout(() => {
                    window.location.href = finalUrl;
                  }, 2000);
                } else {
                  // If payment link not found, redirect to pricing
                  console.error('Payment link not found for selected plan');
                  setTimeout(() => {
                    window.location.href = '/#pricing';
                  }, 2000);
                }
              } else {
                // If no plan selected, redirect to pricing section
                console.log('No plan selected, redirecting to pricing section');
                setTimeout(() => {
                  window.location.href = '/#pricing';
                }, 2000);
              }
              return; // Exit early
            }
          }
        } catch (uidError) {
          console.error('Error getting user ID:', uidError);

          // Check if user selected a plan
          const selectedPlan = localStorage.getItem('selectedPlan');
          const billingCycle = localStorage.getItem('billingCycle') || 'monthly';

          if (selectedPlan) {
            // Get the email from localStorage
            const userEmail = localStorage.getItem('signupEmail');

            if (!userEmail) {
              console.error('No email available for client reference, redirecting to signup');
              setTimeout(() => {
                window.location.href = `/signup?plan=${selectedPlan}`;
              }, 2000);
              return;
            }

            // Use email as client reference for webhook identification
            const emailReference = encodeURIComponent(userEmail);

            // Determine which payment link to use
            let paymentLink;

            if (window.PAYMENT_LINKS) {
              console.log('Using global PAYMENT_LINKS from stripe-config.js');
              console.log('Stripe mode:', window.STRIPE_MODE);

              if (selectedPlan === 'standard') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.STANDARD_ANNUAL : window.PAYMENT_LINKS.STANDARD_MONTHLY;
              } else if (selectedPlan === 'business') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.BUSINESS_ANNUAL : window.PAYMENT_LINKS.BUSINESS_MONTHLY;
              } else if (selectedPlan === 'pro') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.PRO_ANNUAL : window.PAYMENT_LINKS.PRO_MONTHLY;
              }

              console.log(`Using ${window.STRIPE_MODE} mode payment link:`, paymentLink);
            } else {
              console.error('Global PAYMENT_LINKS not found. Make sure stripe-config.js is loaded.');

              // No fallback to hardcoded links - this would cause issues with test/live mode
              console.error('Cannot proceed without payment links from stripe-config.js');
              setError('Configuration error. Please refresh the page and try again.');
              return;
            }

            if (paymentLink) {
              // Add email reference to the payment link
              const separator = paymentLink.includes('?') ? '&' : '?';
              const finalUrl = `${paymentLink}${separator}client_reference_id=${emailReference}&email=${emailReference}`;

              console.log('Redirecting to Stripe payment page with email reference:', finalUrl);

              // Clear the selected plan from localStorage
              localStorage.removeItem('selectedPlan');
              localStorage.removeItem('billingCycle');

              // Redirect to Stripe payment page
              setTimeout(() => {
                window.location.href = finalUrl;
              }, 2000);
            } else {
              // If payment link not found, redirect to pricing
              console.error('Payment link not found for selected plan');
              setTimeout(() => {
                window.location.href = '/#pricing';
              }, 2000);
            }
          } else {
            // If no plan selected, redirect to pricing section
            console.log('No plan selected, redirecting to pricing section');
            setTimeout(() => {
              window.location.href = '/#pricing';
            }, 2000);
          }
          return; // Exit early
        }

        // Check if user came from payment
        const stripeSessionId = localStorage.getItem('stripeSessionId');

        if (stripeSessionId || fromPayment) {
          // If from payment, redirect to onboarding even if there's an error
          setTimeout(() => {
            window.location.href = '/onboarding';
          }, 2000);
        } else {
          // Check if user came from plan selection
          const selectedPlan = localStorage.getItem('selectedPlan');
          const billingCycle = localStorage.getItem('billingCycle');

          if (selectedPlan) {
            // Store the user ID for the payment process and webhook
            localStorage.setItem('userId', userId);

            // Determine which payment link to use
            let paymentLink;

            if (window.PAYMENT_LINKS) {
              console.log('Using global PAYMENT_LINKS from stripe-config.js');
              console.log('Stripe mode:', window.STRIPE_MODE);

              if (selectedPlan === 'standard') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.STANDARD_ANNUAL : window.PAYMENT_LINKS.STANDARD_MONTHLY;
              } else if (selectedPlan === 'business') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.BUSINESS_ANNUAL : window.PAYMENT_LINKS.BUSINESS_MONTHLY;
              } else if (selectedPlan === 'pro') {
                paymentLink = billingCycle === 'annual' ? window.PAYMENT_LINKS.PRO_ANNUAL : window.PAYMENT_LINKS.PRO_MONTHLY;
              }

              console.log(`Using ${window.STRIPE_MODE} mode payment link:`, paymentLink);
            } else {
              console.error('Global PAYMENT_LINKS not found. Make sure stripe-config.js is loaded.');

              // No fallback to hardcoded links - this would cause issues with test/live mode
              console.error('Cannot proceed without payment links from stripe-config.js');
              setError('Configuration error. Please refresh the page and try again.');
              return;
            }

            if (paymentLink) {
              // Add user ID as a parameter to the payment link
              const separator = paymentLink.includes('?') ? '&' : '?';
              const finalUrl = `${paymentLink}${separator}client_reference_id=${userId}&userId=${userId}`;

              console.log('Redirecting to Stripe payment page:', finalUrl);

              // Clear the selected plan from localStorage
              localStorage.removeItem('selectedPlan');
              localStorage.removeItem('billingCycle');

              // Redirect to Stripe payment page
              setTimeout(() => {
                window.location.href = finalUrl;
              }, 2000);
            } else {
              // Fallback to onboarding if payment link not found
              console.error('Payment link not found for selected plan');
              setTimeout(() => {
                window.location.href = '/onboarding';
              }, 2000);
            }
          } else {
            // Always redirect to onboarding
            console.log('No plan selected, redirecting to onboarding page');
            setTimeout(() => {
              window.location.href = '/onboarding';
            }, 2000);
          }
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError("Failed to create an account: " + error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 2
    }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Create Account
        </Typography>

        {fromPayment && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your payment was successful! Please create an account to continue.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign Up'}
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link to="/login" style={{ textDecoration: 'none' }}>
                Log In
              </Link>
            </Typography>
          </Box>
        </Box>

        {signupSuccess && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Account created successfully!
            </Alert>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/onboarding')}
              fullWidth
            >
              Continue to Setup
            </Button>
          </Box>
        )}

        {loading && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading may take a few moments...
            </Typography>
            <Button
              variant="text"
              color="secondary"
              onClick={() => navigate('/fix-connection')}
              sx={{ mt: 1 }}
            >
              Having trouble? Click here
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
